/**
 * SOLID Review
 * S — Strong. One concern: keeping the client's table level with the server's — its events, the pushed view, a
 *     reconnect's resync and the lobby's reads — each built from the snapshot as a `ClientTable`, asked, and answered
 *     through a `ClientDecision`. The rules, the guards that drop an event included, are the aggregate's.
 * O — Good. A new event or catch-up read is a method and a row here, and its rule on the aggregate.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One role, `IServiceDecisionTable`: the Facade sees the table's sync ops and nothing else.
 * D — Strong. The table aggregate, the decision builder, the server's dice ranking and the schemas.
 */
import { ClientTable } from '../../table/ClientTable';
import { DiceRanking } from '../../dice/DiceRanking';
import { ClientDecision, type IConfigClientDecision, type TClientDecision } from '../ClientDecision';
import type { IServiceDecisionTable, TDecisionRows } from '../IServiceDecisionTable';
import type { IPropsClientTableInput } from '../../ports/schemas/ClientTable';
import type {
  IPayloadRoundStarted, IPayloadClaimAnnounced, IPayloadCupLifted, IPayloadDiceRolled, IPayloadRoomRoster, IPayloadPresence,
  IPayloadRoomAbandoned, IPayloadDecisionCountdown, IPayloadConnectivity, IPayloadTableView, IEntityTableAck,
  IEntityConnectivity, IEntityTableViewOutcome,
} from '../../ports/schemas/TableEvents';
import type {
  ICommandActiveRoundRestored, ICommandSuspendedReconciled, ICommandLobbyActiveRound, ICommandLobbyRoomLoaded,
  IPayloadResync, IPayloadRoomRead,
} from '../../ports/schemas/TableMoves';

/** The ops this service answers for on the client Domain's lollipop. */
type TTableSyncOp =
  | 'roundStarted' | 'claimAnnounced' | 'cupLifted' | 'diceRolled' | 'roomUpdated' | 'playerSuspended' | 'playerResumed'
  | 'roomAbandoned' | 'decisionCountdown' | 'connectivity' | 'tableView' | 'planResync' | 'activeRoundRestored'
  | 'suspendedReconciled' | 'planLobbyRead' | 'lobbyActiveRound' | 'lobbyRoomLoaded';

/** A round's events move the table: the store stamps the activity. The room's own facts do not. */
const MOVED: IConfigClientDecision = { markActivity: true };
const STILL: IConfigClientDecision = {};

/**
 * THE TABLE KEPT LEVEL WITH THE SERVER'S (frontend-domain-oo P5, 2026-10-26; handbook §28.272) — the seventeen decide
 * functions of `decisions/table/serverEvents.ts` and `catchUp.ts` as a service (FE1): the server's events, once
 * subscription callbacks in components and hooks; the pushed table view (`reconcileTableView`); the reconnect's
 * resync (`useResyncActiveRound`); the lobby's reads (`useLobbyRoomSync`, `useLobbyActiveRoundPoll`). An event the
 * table's guards drop answers `applied: false` and changes nothing.
 */
export class TableSync implements IServiceDecisionTable<TTableSyncOp> {
  /** Its decisions, by op: what `ClientDecisions` merges and answers through. */
  readonly decisions: TDecisionRows<TTableSyncOp> = {
    roundStarted: (input, event) => this.roundStarted(input, event),
    claimAnnounced: (input, event) => this.claimAnnounced(input, event),
    cupLifted: (input, event) => this.cupLifted(input, event),
    diceRolled: (input, event) => this.diceRolled(input, event),
    roomUpdated: (input, event) => this.roomUpdated(input, event),
    playerSuspended: (input, event) => this.playerSuspended(input, event),
    playerResumed: (input, event) => this.playerResumed(input, event),
    roomAbandoned: (input, event) => this.roomAbandoned(input, event),
    decisionCountdown: (input, event) => this.decisionCountdown(input, event),
    connectivity: (input, event) => this.connectivity(input, event),
    tableView: (input, view) => this.tableView(input, view),
    planResync: (input) => this.planResync(input),
    activeRoundRestored: (input, command) => this.activeRoundRestored(input, command),
    suspendedReconciled: (input, command) => this.suspendedReconciled(input, command),
    planLobbyRead: (input) => this.planLobbyRead(input),
    lobbyActiveRound: (input, command) => this.lobbyActiveRound(input, command),
    lobbyRoomLoaded: (input, command) => this.lobbyRoomLoaded(input, command),
  };

  // ── The server's events ────────────────────────────────────────────────────────────────────────────

  roundStarted(input: IPropsClientTableInput, event: IPayloadRoundStarted): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.roundStarted(event));
  }

  claimAnnounced(input: IPropsClientTableInput, event: IPayloadClaimAnnounced): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.claimAnnounced(event));
  }

  cupLifted(input: IPropsClientTableInput, event: IPayloadCupLifted): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.cupLifted(event, DiceRanking.of(input.diceRanking)));
  }

  diceRolled(input: IPropsClientTableInput, event: IPayloadDiceRolled): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.diceRolled(event));
  }

  roomUpdated(input: IPropsClientTableInput, event: IPayloadRoomRoster): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, STILL).acked(table.roomUpdated(event));
  }

  playerSuspended(input: IPropsClientTableInput, event: IPayloadPresence): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, STILL).acked(table.playerSuspended(event));
  }

  playerResumed(input: IPropsClientTableInput, event: IPayloadPresence): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, STILL).acked(table.playerResumed(event));
  }

  roomAbandoned(input: IPropsClientTableInput, event: IPayloadRoomAbandoned): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, STILL).acked(table.roomAbandoned(event));
  }

  decisionCountdown(input: IPropsClientTableInput, event: IPayloadDecisionCountdown): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, STILL).acked(table.countdownTicked(event));
  }

  /** The websocket changed state; the answer says whether a resync is owed (the table's latch). */
  connectivity(input: IPropsClientTableInput, event: IPayloadConnectivity): TClientDecision<IEntityConnectivity> {
    const table = ClientTable.fromSnapshot(input.table);
    const dto: IEntityConnectivity = { resync: table.connectionChanged(event.state) };
    return ClientDecision.ofTable(input, table).answering(dto);
  }

  // ── Catching up ────────────────────────────────────────────────────────────────────────────────────

  /** A pushed view: ignored, stale, in sync, or reconciled — a view the table dropped changes nothing. */
  tableView(input: IPropsClientTableInput, view: IPayloadTableView): TClientDecision<IEntityTableViewOutcome> {
    const table = ClientTable.fromSnapshot(input.table);
    const dto: IEntityTableViewOutcome = { outcome: table.tableView(view, DiceRanking.of(input.diceRanking)) };
    return ClientDecision.ofTable(input, table).answering(dto);
  }

  planResync(input: IPropsClientTableInput): TClientDecision<IPayloadResync> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planResync());
  }

  activeRoundRestored(input: IPropsClientTableInput, command: ICommandActiveRoundRestored): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).acked(table.activeRoundRestored(command, DiceRanking.of(input.diceRanking)));
  }

  suspendedReconciled(input: IPropsClientTableInput, command: ICommandSuspendedReconciled): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).acked(table.suspendedReconciled(command));
  }

  planLobbyRead(input: IPropsClientTableInput): TClientDecision<IPayloadRoomRead> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planLobbyRead());
  }

  lobbyActiveRound(input: IPropsClientTableInput, command: ICommandLobbyActiveRound): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).acked(table.lobbyActiveRound(command));
  }

  lobbyRoomLoaded(input: IPropsClientTableInput, command: ICommandLobbyRoomLoaded): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).acked(table.lobbyRoomLoaded(command));
  }
}
